import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  Send,
  Sparkles,
  RotateCcw,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  AlertCircle,
  ShieldCheck,
  X,
} from 'lucide-react'
import { tokens } from '@/theme'
import { useChatContext, chatIsActive } from '@/api/foundry/ChatProvider'
import { displayForTool } from '@/api/foundry/toolDisplayNames'
import type { AssistantMessage, ChatMessage, ChatStatus, ToolCall } from '@/api/foundry/types'
import { PageHero, pageHeroStyles } from '@/components/layout/PageHero'
import { OrionScene } from '@/components/visual/OrionScene'

/**
 * Phase 3 Stage 12 — chat surface backed by Azure AI Foundry.
 *
 * Streaming-first: the SPA opens an SSE stream directly to Foundry's
 * agent run endpoint and renders deltas incrementally. The architecture
 * keeps every Orion-owned proxy out of the response path; see the plan
 * doc's "Streaming is non-negotiable" section for why.
 *
 * Rendering: message list is memoised so token-delta storms only re-render
 * the streaming assistant bubble, not the whole conversation history.
 */
export function ChatView() {
  // Chat hook is mounted once in AuthenticatedShell via ChatProvider —
  // see App.tsx. Pulling it from context (instead of useFoundryChat()
  // here) means state survives navigation: kick off a question, pop
  // over to /initiatives, come back, the answer is already streamed.
  const chat = useChatContext()

  if (chat.configMissing) {
    return <ConfigPendingScreen />
  }

  // Chat focus mode: once a conversation is active (a message exists or a run
  // is in flight), collapse the "Ask Orion" hero upward so the thread fills
  // the space. The matching footer collapse lives in the App shell. Collapse
  // styling + reduced-motion guard is the `.orion-collapse` block injected in
  // App.tsx. See docs/sps/chat-focus-mode-requirements.md.
  const heroCollapsed = chatIsActive(chat)

  return (
    <>
      <style>{pageHeroStyles}</style>
      <div
        className={`orion-collapse orion-collapse--hero${heroCollapsed ? ' is-collapsed' : ''}`}
        aria-hidden={heroCollapsed || undefined}
      >
        <div className="orion-collapse__inner">
          <PageHero
            headline="Ask Orion"
            headlineSize={30}
            padY={34}
            lede="Streaming answers grounded in the same audited initiative data you see in the structured views."
            aside={<OrionScene variant="mark" height={150} />}
            asideWidth={280}
          />
        </div>
      </div>

      {/* The page itself scrolls and the composer sticks to the viewport
          bottom — no inner scroll container. That keeps the thread readable
          at any length without a nested scrollbar, and it's what the design
          shows. */}
      <div
        className="orion-pad"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: 920,
          margin: '0 auto',
          padding: `24px ${tokens.containerPadX}px 0`,
          boxSizing: 'border-box',
          fontFamily: tokens.fontBody,
        }}
      >
        <ThreadMeta
          hasMessages={chat.messages.length > 0}
          startedAt={chat.threadStartedAt}
          onNewThread={chat.newThread}
        />
        <MessageList
          messages={chat.messages}
          status={chat.status}
          onApprove={() => void chat.respondToApproval(true)}
          onDeny={() => void chat.respondToApproval(false)}
          onTrustForSession={chat.trustToolForSession}
          onAuthorize={chat.authorizeAndRetry}
        />
        <StatusBar status={chat.status} onRetry={() => chat.newThread()} />
        <ChatInput
          onSend={chat.send}
          disabled={
            chat.status.kind === 'sending' ||
            chat.status.kind === 'streaming' ||
            chat.status.kind === 'tool_approval_required' ||
            chat.status.kind === 'consent_required' ||
            chat.status.kind === 'tool_outputs_required'
          }
          onCancel={chat.cancel}
          canCancel={chat.status.kind === 'streaming'}
        />
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Configuration-pending fallback (rendered before Step 3 wires up the agent)
// ---------------------------------------------------------------------------

function ConfigPendingScreen() {
  return (
    <div
      className="orion-pad"
      style={{
        maxWidth: tokens.containerMax,
        margin: '0 auto',
        padding: `96px ${tokens.containerPadX}px`,
        fontFamily: tokens.fontDisplay,
        textAlign: 'center',
      }}
    >
      <div style={{ width: 48, height: 2, background: tokens.gold, margin: '0 auto 24px' }} />
      <Sparkles size={32} strokeWidth={1.4} color={tokens.ink3} style={{ marginBottom: 24 }} />
      <h1
        style={{
          fontSize: tokens.sizeH1,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          marginBottom: 16,
        }}
      >
        Chat configuration pending
      </h1>
      <p
        style={{
          fontSize: tokens.sizeBodyL,
          color: tokens.ink2,
          maxWidth: 560,
          margin: '0 auto',
          lineHeight: 1.65,
        }}
      >
        The Foundry agent powering this chat is being provisioned. Once the agent is wired up in the
        Foundry portal (Phase 3 Stage 12, Step 3), this page will start a streaming conversation
        backed by the same per-user audit chain you see in the structured views.
      </p>
      <p
        style={{
          fontSize: tokens.sizeBody,
          color: tokens.ink3,
          maxWidth: 560,
          margin: '24px auto 0',
          lineHeight: 1.65,
        }}
      >
        Use the navigation above to browse initiatives, domains, and capabilities in the meantime.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Thread meta: when this thread started + "New chat"
// ---------------------------------------------------------------------------

function ThreadMeta({
  hasMessages,
  startedAt,
  onNewThread,
}: {
  hasMessages: boolean
  startedAt: number | null
  onNewThread: () => void
}) {
  const [hover, setHover] = useState(false)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 20,
      }}
    >
      <span
        style={{
          fontFamily: tokens.fontDisplay,
          fontSize: 11,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: tokens.ink3,
        }}
      >
        {hasMessages && startedAt !== null
          ? `Thread · ${formatThreadStart(startedAt)}`
          : 'New thread'}
      </span>
      {hasMessages && (
        <button
          onClick={onNewThread}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: hover ? tokens.paper2 : 'transparent',
            color: tokens.ink2,
            border: `1px solid ${tokens.line2}`,
            borderRadius: tokens.radiusMd,
            padding: '6px 12px',
            fontFamily: tokens.fontDisplay,
            fontSize: 11.5,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: `background ${tokens.durFast} ${tokens.easeStandard}`,
          }}
        >
          <RotateCcw size={13} strokeWidth={1.6} />
          New chat
        </button>
      )}
    </div>
  )
}

/**
 * "today 14:02" for a thread started today, otherwise a short date. The
 * thread is in-memory only, so "today" is the overwhelmingly common case —
 * but a tab left open overnight shouldn't claim yesterday's chat is today's.
 */
function formatThreadStart(startedAt: number): string {
  const time = new Date(startedAt).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
  const isToday = new Date(startedAt).toDateString() === new Date().toDateString()
  if (isToday) return `today ${time}`
  return `${new Date(startedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })} ${time}`
}

// ---------------------------------------------------------------------------
// Message list — memoised so token deltas only re-render the streaming bubble
// ---------------------------------------------------------------------------

const streamingCursor = `
@keyframes orion-cursor-blink {
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
}
@keyframes orion-thinking {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}
@keyframes orion-spin {
  to { transform: rotate(360deg); }
}
/* Markdown rendering inside assistant bubbles. We tighten the default
   browser margins so paragraphs/headings/lists don't blow out the
   bubble's compact look. First/last children touch the bubble edge. */
.orion-md > *:first-child { margin-top: 0; }
.orion-md > *:last-child { margin-bottom: 0; }
.orion-md p { margin: 0 0 8px; }
.orion-md p:last-child { margin-bottom: 0; }
.orion-md h1, .orion-md h2, .orion-md h3, .orion-md h4 {
  font-family: inherit;
  font-weight: 600;
  margin: 12px 0 6px;
  line-height: 1.3;
}
.orion-md h1 { font-size: 1.15em; }
.orion-md h2 { font-size: 1.1em; }
.orion-md h3, .orion-md h4 { font-size: 1.05em; }
.orion-md ul, .orion-md ol { margin: 0 0 8px; padding-left: 22px; }
.orion-md li { margin: 2px 0; }
.orion-md li > p { margin: 0; }
.orion-md code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.92em;
  background: rgba(0, 0, 0, 0.05);
  padding: 1px 4px;
  border-radius: 3px;
}
.orion-md pre {
  margin: 6px 0;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  overflow-x: auto;
  font-size: 0.88em;
  line-height: 1.5;
}
.orion-md pre code {
  background: none;
  padding: 0;
  border-radius: 0;
}
.orion-md a { color: currentColor; text-decoration: underline; text-underline-offset: 2px; }
.orion-md blockquote {
  margin: 6px 0;
  padding: 4px 12px;
  border-left: 3px solid rgba(0, 0, 0, 0.15);
  color: inherit;
  opacity: 0.85;
}
.orion-md table {
  border-collapse: collapse;
  margin: 6px 0;
  font-size: 0.95em;
}
.orion-md th, .orion-md td {
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 4px 8px;
  text-align: left;
}
.orion-md th { background: rgba(0, 0, 0, 0.04); font-weight: 600; }
.orion-md hr { border: none; border-top: 1px solid rgba(0, 0, 0, 0.12); margin: 10px 0; }
`

function MessageList({
  messages,
  status,
  onApprove,
  onDeny,
  onTrustForSession,
  onAuthorize,
}: {
  messages: ChatMessage[]
  status: ChatStatus
  onApprove: () => void
  onDeny: () => void
  onTrustForSession: (toolName: string) => void
  onAuthorize: () => void
}) {
  // Keep the newest content in view as it streams. The page itself scrolls
  // (no inner container) and the composer is `position: sticky; bottom: 0`,
  // painted over the bottom strip of the viewport. Aligning an end sentinel
  // to the viewport bottom (scrollIntoView block:'end') therefore left the
  // last ~5 lines hidden behind the composer. Instead, scroll the window to
  // its max: the sticky composer also occupies its own space in normal flow
  // at the document bottom, so max-scroll seats it against the viewport
  // bottom with the newest text just above it — self-adjusting to the
  // composer's real height. Instant, in a layout effect, so it pins before
  // paint. Users can scroll up manually; we don't preserve position
  // mid-stream (that's a later polish).
  useLayoutEffect(() => {
    if (messages.length === 0) return
    window.scrollTo({ top: document.documentElement.scrollHeight })
  }, [messages, status])

  if (messages.length === 0 && status.kind === 'idle') {
    return <EmptyState />
  }

  return (
    <>
      <style>{streamingCursor}</style>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          paddingBottom: 24,
        }}
      >
        {messages.map(m => (
          <MessageBubble
            key={m.id}
            message={m}
            onApprove={onApprove}
            onDeny={onDeny}
            onTrustForSession={onTrustForSession}
          />
        ))}
        {status.kind === 'consent_required' && <ConsentBubble onAuthorize={onAuthorize} />}
        {needsThinkingPulse(status, messages) && <ThinkingPulse />}
      </div>
    </>
  )
}

/**
 * The pulse should show whenever the user is waiting on something the
 * agent hasn't yet started rendering: between send and first SSE frame
 * (`sending`), and also between `response.created` and the first content
 * delta (`streaming` with no assistant bubble yet, or with one that has
 * neither text nor tool calls). Without the second branch the UI looks
 * frozen while Foundry's MCP tools/list step is in flight (often
 * 500ms+).
 */
function needsThinkingPulse(status: ChatStatus, messages: ChatMessage[]): boolean {
  if (status.kind === 'sending') return true
  if (status.kind === 'tool_outputs_required') return true
  if (status.kind !== 'streaming') return false
  const last = messages[messages.length - 1]
  if (!last) return true
  if (last.role === 'user') return true
  return last.text.length === 0 && last.toolCalls.length === 0
}

// ---------------------------------------------------------------------------
// Single message bubble — memoised on message identity so stable messages
// don't re-render when the streaming one changes.
// ---------------------------------------------------------------------------

const MessageBubble = memo(function MessageBubble({
  message,
  onApprove,
  onDeny,
  onTrustForSession,
}: {
  message: ChatMessage
  onApprove: () => void
  onDeny: () => void
  onTrustForSession: (toolName: string) => void
}) {
  if (message.role === 'user') {
    return <UserBubble text={message.text} />
  }
  return (
    <AssistantBubble
      message={message}
      onApprove={onApprove}
      onDeny={onDeny}
      onTrustForSession={onTrustForSession}
    />
  )
})

function UserBubble({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <div
        style={{
          maxWidth: '78%',
          background: tokens.ink,
          color: tokens.paper,
          padding: '11px 15px',
          borderRadius: tokens.radiusMd,
          fontSize: tokens.sizeBody,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          fontFamily: tokens.fontBody,
        }}
      >
        {text}
      </div>
    </div>
  )
}

function AssistantBubble({
  message,
  onApprove,
  onDeny,
  onTrustForSession,
}: {
  message: AssistantMessage
  onApprove: () => void
  onDeny: () => void
  onTrustForSession: (toolName: string) => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {message.toolCalls.map(tc => (
        <ToolCallRow
          key={tc.id}
          toolCall={tc}
          onApprove={onApprove}
          onDeny={onDeny}
          onTrustForSession={onTrustForSession}
        />
      ))}
      {(message.text.length > 0 || !message.toolCalls.length) && (
        <div
          className="orion-md"
          style={{
            maxWidth: '88%',
            background: tokens.paper2,
            color: tokens.ink,
            padding: '14px 16px',
            borderRadius: tokens.radiusMd,
            fontSize: tokens.sizeBody,
            lineHeight: 1.6,
            fontFamily: tokens.fontBody,
          }}
        >
          <AssistantMarkdown text={message.text} />
          {message.isStreaming && (
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 8,
                height: '1em',
                marginLeft: 2,
                verticalAlign: 'text-bottom',
                background: tokens.ink3,
                animation: 'orion-cursor-blink 1s steps(1, end) infinite',
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Assistant markdown — small wrapper around react-markdown with the GFM
// plugin (tables, strikethrough, task lists). Memoised so token-delta
// storms only re-parse the active streaming bubble. Links open in a new
// tab; raw HTML is rejected by default (react-markdown without rehype-raw).
// ---------------------------------------------------------------------------

const markdownPlugins = [remarkGfm]

const markdownComponents = {
  a: ({ href, children, ...rest }: ComponentPropsWithoutRef<'a'>) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  ),
}

const AssistantMarkdown = memo(function AssistantMarkdown({ text }: { text: string }) {
  return (
    <ReactMarkdown remarkPlugins={markdownPlugins} components={markdownComponents}>
      {text}
    </ReactMarkdown>
  )
})

// ---------------------------------------------------------------------------
// Tool call row — collapsed by default with the verb form + spinner;
// click to expand the args + result JSON.
// ---------------------------------------------------------------------------

function ToolCallRow({
  toolCall,
  onApprove,
  onDeny,
  onTrustForSession,
}: {
  toolCall: ToolCall
  onApprove: () => void
  onDeny: () => void
  onTrustForSession: (toolName: string) => void
}) {
  const display = displayForTool(toolCall.name)
  const isAwaitingApproval = toolCall.state === 'awaiting_approval'
  // Approval row defaults to expanded so the user can see what they're
  // approving without an extra click. Everything else is collapsed.
  const [expanded, setExpanded] = useState(isAwaitingApproval)
  const isInProgress = toolCall.state === 'in_progress' || toolCall.state === 'dispatching'
  const isFailed = toolCall.state === 'failed'

  const headerLabel = isAwaitingApproval
    ? `Approve ${display.label.toLowerCase()}?`
    : isInProgress
      ? display.verb
      : display.label
  const accent = isAwaitingApproval ? tokens.gold : isFailed ? tokens.statusRed : tokens.line

  return (
    // Collapsed, this is a chip that hugs its label — a quiet note that the
    // agent did something, per the design. Expanded (or awaiting approval)
    // it becomes a full card so the args and result have room.
    <div
      style={{
        alignSelf: 'flex-start',
        maxWidth: '88%',
        border: `1px solid ${accent}`,
        borderRadius: tokens.radiusMd,
        background: tokens.paper,
      }}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '7px 12px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: tokens.fontBody,
          fontSize: 12,
          color: tokens.ink3,
        }}
      >
        {isAwaitingApproval ? (
          <ShieldCheck size={13} strokeWidth={1.8} color={tokens.gold} />
        ) : isInProgress ? (
          <Loader2
            size={13}
            strokeWidth={2}
            color={tokens.gold}
            style={{ animation: 'orion-spin 1s linear infinite' }}
          />
        ) : isFailed ? (
          <AlertCircle size={13} strokeWidth={1.6} color={tokens.statusRed} />
        ) : (
          <Check size={13} strokeWidth={2} color={tokens.statusTeal} />
        )}
        <span style={{ flex: 1, color: isAwaitingApproval ? tokens.ink : tokens.ink3 }}>
          {headerLabel}
        </span>
        {expanded ? (
          <ChevronDown size={12} strokeWidth={1.8} color={tokens.ink4} />
        ) : (
          <ChevronRight size={12} strokeWidth={1.8} color={tokens.ink4} />
        )}
      </button>
      {expanded && (
        <div
          style={{
            borderTop: `1px solid ${tokens.line}`,
            padding: 12,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            color: tokens.ink2,
            background: tokens.paper2,
            overflowX: 'auto',
          }}
        >
          <div style={{ marginBottom: isAwaitingApproval || toolCall.resultJson !== null ? 8 : 0 }}>
            <strong style={{ color: tokens.ink3 }}>Arguments</strong>
            <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>
              {formatArgsForDisplay(toolCall.argsJson)}
            </pre>
          </div>
          {toolCall.resultJson !== null && (
            <div>
              <strong style={{ color: tokens.ink3 }}>Result</strong>
              <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{toolCall.resultJson}</pre>
            </div>
          )}
        </div>
      )}
      {isAwaitingApproval && (
        <ApprovalActions
          toolName={toolCall.name}
          onApprove={onApprove}
          onDeny={onDeny}
          onTrustForSession={onTrustForSession}
        />
      )}
    </div>
  )
}

function formatArgsForDisplay(argsJson: string): string {
  if (!argsJson) return '(none)'
  try {
    return JSON.stringify(JSON.parse(argsJson), null, 2)
  } catch {
    return argsJson
  }
}

// ---------------------------------------------------------------------------
// Approval actions — inline footer under an `awaiting_approval` tool row.
// Session-trust toggle is plain in-memory state on the hook; checked-then-
// approve adds the tool to the trusted-set so future calls auto-approve.
// ---------------------------------------------------------------------------

function ApprovalActions({
  toolName,
  onApprove,
  onDeny,
  onTrustForSession,
}: {
  toolName: string
  onApprove: () => void
  onDeny: () => void
  onTrustForSession: (toolName: string) => void
}) {
  const [trustForSession, setTrustForSession] = useState(false)
  const display = displayForTool(toolName)

  const approve = useCallback(() => {
    if (trustForSession) onTrustForSession(toolName)
    onApprove()
  }, [trustForSession, toolName, onTrustForSession, onApprove])

  return (
    <div
      style={{
        borderTop: `1px solid ${tokens.line}`,
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: tokens.ink2,
          cursor: 'pointer',
          fontFamily: tokens.fontBody,
          flex: 1,
          minWidth: 0,
        }}
      >
        <input
          type="checkbox"
          checked={trustForSession}
          onChange={e => setTrustForSession(e.target.checked)}
          style={{ accentColor: tokens.gold, cursor: 'pointer' }}
        />
        Trust {display.label.toLowerCase()} for this session
      </label>
      <button
        onClick={onDeny}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          color: tokens.ink2,
          border: `1px solid ${tokens.line2}`,
          borderRadius: tokens.radiusSm,
          padding: '6px 12px',
          fontFamily: tokens.fontDisplay,
          fontSize: 11.5,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        <X size={12} strokeWidth={1.8} />
        Deny
      </button>
      <button
        onClick={approve}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: tokens.gold,
          color: tokens.paper,
          border: 'none',
          borderRadius: tokens.radiusSm,
          padding: '6px 12px',
          fontFamily: tokens.fontDisplay,
          fontSize: 11.5,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        <Check size={12} strokeWidth={2} />
        Approve
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Consent bubble — rendered inline in the chat (assistant-side style) when
// Foundry's MCP server needs OAuth Identity Passthrough consent. Mirrors
// the message-list shape so the prompt feels like a turn in the
// conversation, not a system banner. Clicking Authorize opens a popup
// against the consent URL; the hook watches for the popup to close and
// auto-resends the user's previous message.
// ---------------------------------------------------------------------------

function ConsentBubble({ onAuthorize }: { onAuthorize: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          maxWidth: '88%',
          background: tokens.paper2,
          color: tokens.ink,
          padding: '12px 14px',
          borderRadius: tokens.radiusMd,
          border: `1px solid ${tokens.gold}`,
          fontSize: tokens.sizeBody,
          lineHeight: 1.6,
          fontFamily: tokens.fontBody,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <ShieldCheck size={16} strokeWidth={1.8} color={tokens.gold} />
          <strong style={{ fontWeight: tokens.weightDemi }}>One-time sign-in needed</strong>
        </div>
        <p style={{ margin: '0 0 12px', color: tokens.ink2 }}>
          I need permission to access your business-context data on your behalf. Sign in once and
          I&apos;ll continue where we left off.
        </p>
        <button
          onClick={onAuthorize}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: tokens.gold,
            color: tokens.paper,
            border: 'none',
            borderRadius: tokens.radiusSm,
            padding: '8px 14px',
            fontFamily: tokens.fontDisplay,
            fontSize: 12,
            fontWeight: tokens.weightDemi,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          <ExternalLink size={13} strokeWidth={1.8} />
          Authorize
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Thinking pulse (between user send and first SSE event)
// ---------------------------------------------------------------------------

function ThinkingPulse() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 4px' }}>
      {[0, 200, 400].map(delay => (
        <span
          key={delay}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: tokens.ink3,
            animation: `orion-thinking 1.2s ease-in-out ${delay}ms infinite`,
          }}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status bar (errors + consent link)
// ---------------------------------------------------------------------------

function StatusBar({ status, onRetry }: { status: ChatStatus; onRetry: () => void }) {
  if (
    status.kind === 'idle' ||
    status.kind === 'sending' ||
    status.kind === 'streaming' ||
    // The approval card renders inline on the tool row; the consent
    // prompt renders as an inline chat bubble — both replace what used
    // to be a top-banner here.
    status.kind === 'tool_approval_required' ||
    status.kind === 'consent_required' ||
    // Function-tool dispatch is autonomous and short-lived — the inline
    // tool rows in the bubble show `dispatching`, no banner needed.
    status.kind === 'tool_outputs_required'
  ) {
    return null
  }
  return (
    <div
      style={{
        marginBottom: 12,
        padding: 12,
        border: `1px solid ${tokens.statusRed}`,
        borderRadius: tokens.radiusMd,
        background: tokens.paper2,
        fontSize: tokens.sizeBody,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <AlertCircle size={16} strokeWidth={1.6} color={tokens.statusRed} />
      <span style={{ color: tokens.ink, flex: 1 }}>{status.message}</span>
      <button
        onClick={onRetry}
        style={{
          background: 'transparent',
          color: tokens.ink2,
          border: `1px solid ${tokens.line2}`,
          borderRadius: tokens.radiusSm,
          padding: '4px 10px',
          fontFamily: tokens.fontDisplay,
          fontSize: 11,
          fontWeight: tokens.weightDemi,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        Reset
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty state with example prompts
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '48px 16px',
        textAlign: 'center',
      }}
    >
      <Sparkles size={28} strokeWidth={1.4} color={tokens.ink3} style={{ marginBottom: 12 }} />
      <h2
        style={{
          fontSize: tokens.sizeH3,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          margin: '0 0 8px',
          fontFamily: tokens.fontDisplay,
        }}
      >
        Ask anything about your business context
      </h2>
      <p
        style={{
          fontSize: tokens.sizeBody,
          color: tokens.ink2,
          maxWidth: 480,
          lineHeight: 1.65,
          margin: 0,
        }}
      >
        Search initiatives, look up domains and capabilities, create or update records. Every change
        is audited under your account.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Input — submit on Enter, newline on Shift+Enter, cancel button when streaming
// ---------------------------------------------------------------------------

function ChatInput({
  onSend,
  disabled,
  onCancel,
  canCancel,
}: {
  onSend: (message: string) => Promise<void>
  disabled: boolean
  onCancel: () => void
  canCancel: boolean
}) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const submit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    setValue('')
    void onSend(trimmed)
    // Keep focus after submit — `setValue('')` doesn't drop focus on
    // its own, but if the submit button was clicked instead of Enter,
    // we want the textarea to re-take focus so the user can keep typing.
    // `preventScroll` so re-focusing the sticky composer never nudges the
    // window scroll away from the streaming answer.
    textareaRef.current?.focus({ preventScroll: true })
  }, [value, disabled, onSend])

  // Focus on mount so the user can type immediately when the chat
  // view opens. Also refocus whenever the input transitions from
  // "disabled" (sending / consent / approval) back to ready — this
  // recovers focus that may have been parked on the Authorize popup
  // or the Approve/Deny buttons.
  useEffect(() => {
    if (!disabled) textareaRef.current?.focus({ preventScroll: true })
  }, [disabled])

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        background: tokens.paper,
        padding: '16px 0 28px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 14,
          alignItems: 'flex-end',
          background: tokens.paper,
          border: `1px solid ${tokens.line2}`,
          borderRadius: tokens.radiusLg,
          boxShadow: '0 12px 32px -20px rgba(17,17,17,0.3)',
          padding: '14px 14px 14px 18px',
        }}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              submit()
            }
          }}
          placeholder={
            disabled
              ? 'Agent is responding…'
              : 'Ask about your initiatives, domains, or capabilities…'
          }
          // Intentionally NOT setting `disabled` here. A disabled textarea
          // loses focus (browsers blur disabled elements), and re-enabling
          // doesn't restore it — the user has to click back in. Keep the
          // textarea always-interactive so focus survives a send round-
          // trip; the send button + submit() guard handle the actual gate.
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            border: 'none',
            padding: 0,
            fontSize: tokens.sizeBody,
            fontFamily: tokens.fontBody,
            color: tokens.ink,
            background: 'transparent',
            outline: 'none',
            minHeight: 40,
            maxHeight: 160,
            lineHeight: 1.5,
            alignSelf: 'center',
          }}
        />
        {!canCancel && (
          <span
            aria-hidden
            style={{
              alignSelf: 'center',
              fontSize: 11,
              color: tokens.ink4,
              letterSpacing: '0.06em',
              whiteSpace: 'nowrap',
            }}
          >
            ⏎ to send
          </span>
        )}
        {canCancel ? (
          <button
            onClick={onCancel}
            aria-label="Cancel"
            style={{
              background: 'transparent',
              color: tokens.ink2,
              border: `1px solid ${tokens.line2}`,
              borderRadius: tokens.radiusMd,
              padding: '0 16px',
              height: 40,
              fontFamily: tokens.fontDisplay,
              fontSize: 11.5,
              fontWeight: tokens.weightDemi,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={disabled || value.trim().length === 0}
            aria-label="Send"
            style={{
              background: disabled || value.trim().length === 0 ? tokens.line2 : tokens.gold,
              color: tokens.paper,
              border: 'none',
              borderRadius: tokens.radiusMd,
              width: 40,
              height: 40,
              cursor: disabled || value.trim().length === 0 ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: `background ${tokens.durFast} ${tokens.easeStandard}`,
            }}
          >
            <Send size={16} strokeWidth={1.8} />
          </button>
        )}
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          color: tokens.ink4,
          fontFamily: tokens.fontBody,
          textAlign: 'center',
        }}
      >
        Answers are grounded in Orion&rsquo;s audited data. Verify before acting on critical
        decisions.
      </div>
    </div>
  )
}
